'use client';

import { cn } from '@core/utils';
import { useEffect, useState } from 'react';

import { useSubscribedState } from '@/hooks/useSubscribedState';

import styles from './AnimatedNumber.module.scss';

type Direction = 'up' | 'down';

interface AnimatedNumberProps {
  value: number;
  direction: Direction;
  className?: string;
  suffix?: string;
  animate?: boolean;
}

const rand = (min: number, max: number) => Math.random() * (max - min) + min;

const format = (n: number) =>
  Math.max(0, Math.round(n / 100) * 100).toLocaleString('ko-KR');

const CONFIG: Record<
  Direction,
  { interval: [number, number]; delta: [number, number] }
> = {
  up: { interval: [800, 2000], delta: [100, 800] },
  down: { interval: [1500, 4000], delta: [100, 500] },
};

export const AnimatedNumber = ({
  value,
  direction,
  className,
  suffix = '',
  animate = true,
}: AnimatedNumberProps) => {
  const safeValue = Math.max(0, value);
  const [counter, setCounter] = useSubscribedState(safeValue, [safeValue]);
  const [pulse, setPulse] = useState(false);
  const config = CONFIG[direction];

  useEffect(() => {
    if (!animate) return;

    let id: NodeJS.Timeout;

    const tick = () => {
      id = setTimeout(
        () => {
          setCounter((prev) => {
            const delta = Math.floor(rand(...config.delta)) * 100;

            return direction === 'up'
              ? prev + delta
              : Math.max(0, prev - delta);
          });
          setPulse(true);
          tick();
        },
        rand(...config.interval),
      );
    };

    tick();

    return () => clearTimeout(id);
  }, [direction, config, animate, setCounter]);

  return (
    <span className={className}>
      <span
        className={cn('tabular-nums', styles.number, pulse && styles.pulse)}
        onAnimationEnd={() => setPulse(false)}
      >
        {format(counter)}
      </span>
      {suffix}
    </span>
  );
};
